import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Loader2, PackageOpen } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function MyRentals() {
  const { user } = useAuth();
  const { data: rentals = [], isLoading } = useQuery({
    queryKey: ["rentals"],
    queryFn: async () => (await api.get("/rentals")).data,
  });

  const active = rentals.filter((r) => r.status !== "returned");

  return (
    <div data-testid="my-rentals-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#0F172A]">My Rentals</h1>
        <p className="text-slate-500 mt-1">Units currently assigned to you and your return dates.</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0A2540]" /></div>
      ) : rentals.length === 0 ? (
        <Card className="border-slate-200 shadow-none rounded-md p-12 text-center">
          <PackageOpen className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 font-medium text-[#0F172A]">No rentals linked to your account yet</p>
          <p className="text-sm text-slate-500 mt-1">
            If you expect to see rentals, make sure PC Panda has registered you with this email:
            <span className="font-mono text-[#0A2540]"> {user?.email}</span>
          </p>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-none rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <span className="text-sm text-slate-500">{active.length} active · {rentals.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Asset Tag</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Delivered</TableHead>
                  <TableHead>Expected Return</TableHead>
                  <TableHead>Returned</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rentals.map((r) => (
                  <TableRow key={r.id} data-testid={`my-rental-row-${r.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-mono font-medium text-[#0F172A]">{r.asset_tag}</TableCell>
                    <TableCell className="text-slate-600">{r.device_label}</TableCell>
                    <TableCell className="text-slate-600">{fmtDate(r.delivery_date)}</TableCell>
                    <TableCell className="text-slate-600">{fmtDate(r.expected_return_date)}</TableCell>
                    <TableCell className="text-slate-600">{fmtDate(r.actual_return_date)}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
